<template>
  <div class="iframe-renderer">
    <!-- 외부 maskInner (iframe 바깥쪽만 가림, iframe은 제외) -->
    <div v-if="showExternalMask" class="maskInner"></div>

    <!-- v-if로 iframe 완전 제거/재생성하여 JavaScript 컨텍스트 초기화 -->
    <iframe
      v-if="isIframeVisible"
      ref="iframeRef"
      :src="iframeSrc"
      :key="routeKey"
      :class="['iframe-content', { 'iframe-with-mask': showExternalMask }]"
      title="External Content"
      @load="onIframeLoad"
      @error="onIframeError"
      scrolling="auto"
    />
  </div>
</template>

<script>
// 모듈 수준 싱글톤 상태 (컴포넌트 리마운트에도 유지)
const moduleState = {
  previousPgmId: null,
  currentTabIndex: 0,
};
</script>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { useLoadingStore } from '~/composables/useLoadingStore';
import { shouldSkipLoadingInIframe } from '~/config/api-loading-config';
import { useTabNavigation } from '~/composables/useTabNavigation';
import { useAuthStore } from '~/stores/useAuthStore';
import { getCurrentAppLocale } from '~/composables/useAppLocale';

// ===== 환경 설정 =====
const config = useRuntimeConfig();
const isDev = import.meta.dev || config.public?.isDev;
// 개발: Spring 서버 직접 호출, 운영: 상대 경로 (같은 도메인)
const baseUrl = isDev ? 'http://localhost:8080' : '';

// 조건부 로깅 헬퍼
const devLog = (...args) => {
  if (isDev) console.log('[AsisRenderer]', ...args);
};

// ===== 외부 의존성 =====
const router = useRouter();
const { pendingTabChange, clearPendingTabChange } = useTabNavigation();
const isLoading = useLoadingStore();
const authStore = useAuthStore();

// ===== 세션 체크 헬퍼 =====
// iframe 로드 전에 세션 유효성 확인 (JSP에서 로그인 페이지가 iframe 안에 표시되는 것 방지)
// checkAuth()가 세션 만료 시 자동으로 다이얼로그 표시
const checkSessionBeforeLoad = async () => {
  const isValid = await authStore.checkAuth();
  if (!isValid) {
    devLog('⚠️ 세션 만료 감지 - iframe 로드 중단');
  }
  return isValid;
};

// ===== Props =====
const props = defineProps({
  pgmId: {
    type: String,
    default: null,
  },
  meta: {
    type: String,
    default: null,
  },
});

// ===== 상태 관리 =====
const componentId = Math.random().toString(36).substring(7);
const currentTabIndex = ref(moduleState.currentTabIndex);
const iframeSrc = ref('');
const routeKey = ref('');
const iframeRef = ref(null);
const showExternalMask = ref(false);
const activeRequestCount = ref(0);
const isMounted = ref(false);
const isIframeVisible = ref(true); // iframe 제거/재생성 제어

let cleanupListener = null;
let tabChangedListener = null;
let localeChangedListener = null;

const handleAppLocaleChanged = () => {
  if (!props.pgmId || !isIframeVisible.value) return;
  devLog('🌐 locale 변경 → iframe 새로고침');
  refreshIframe(currentTabIndex.value);
};

// ===== 탭 인덱스 동기화 헬퍼 =====
const updateTabIndex = tabIndex => {
  currentTabIndex.value = tabIndex;
  moduleState.currentTabIndex = tabIndex;
};

// ===== iframe URL 생성 =====
const buildIframeSrc = (pgmId, tabIndex = 0) => {
  if (!pgmId) return '';

  const params = new URLSearchParams();
  params.set('_nuxt', '1'); // Nuxt iframe 요청 식별자
  params.set('pgmId', pgmId);
  params.set('tabIndex', String(tabIndex));
  params.set('layout', 'iframe'); // 경량 레이아웃 사용
  params.set('lang', getCurrentAppLocale()); // 레거시 JSP 세션·UI 언어 동기화
  params.set('_t', String(Date.now())); // 캐시 방지

  // meta 토큰이 있으면 추가
  if (props.meta) {
    params.set('token', props.meta);
  }

  return `${baseUrl}/main.do?${params.toString()}`;
};

// ===== iframe 새로고침 =====
const refreshIframe = async (tabIndex = currentTabIndex.value) => {
  if (!props.pgmId) return;

  devLog(`🔄 refreshIframe [${componentId}]: pgmId=${props.pgmId}, tabIndex=${tabIndex}`);

  // ★ 세션 체크 먼저 수행 (JSP 로그인 페이지가 iframe에 표시되는 것 방지)
  const isSessionValid = await checkSessionBeforeLoad();
  if (!isSessionValid) {
    devLog('❌ 세션 만료로 iframe 로드 중단');
    return;
  }

  // 로딩 시작
  isLoading.value = true;

  // ★ iframe을 완전히 제거하여 JavaScript 컨텍스트 초기화
  isIframeVisible.value = false;

  // DOM에서 완전히 제거될 때까지 대기
  await nextTick();

  // iframe src 설정
  const newSrc = buildIframeSrc(props.pgmId, tabIndex);
  iframeSrc.value = newSrc;
  routeKey.value = `${props.pgmId}_${tabIndex}_${Date.now()}`;

  // ★ iframe 재생성
  isIframeVisible.value = true;

  devLog('⚡ iframe 재생성 완료:', newSrc);
};

// ===== 로딩 상태 관리 =====
const incrementLoading = () => {
  activeRequestCount.value++;
  isLoading.value = true;
};

const decrementLoading = () => {
  activeRequestCount.value = Math.max(0, activeRequestCount.value - 1);
  if (activeRequestCount.value === 0) {
    isLoading.value = false;
  }
};

// ===== postMessage 이벤트 처리 =====
const setupPostMessageListener = () => {
  const handleMessage = event => {
    const { data } = event;
    if (!data?.type) return;

    switch (data.type) {
      case 'MASK_INNER_STATUS':
        showExternalMask.value = data.hasMask;
        break;

      case 'IFRAME_AXIOS_LOADING_START':
        if (!shouldSkipLoadingInIframe(data.url)) {
          incrementLoading();
          devLog('📤 iframe axios 요청 시작:', data.url);
        }
        break;

      case 'IFRAME_AXIOS_LOADING_END':
        if (!shouldSkipLoadingInIframe(data.url)) {
          decrementLoading();
          devLog('✅ iframe axios 요청 완료:', data.url);
        }
        break;

      case 'NAVIGATE_TO_PAGE':
        // iframe 내에서 페이지 전환 요청
        if (data.pgmId) {
          devLog('🚀 iframe에서 페이지 전환 요청:', data.pgmId);
          router.push(`/asis/${data.pgmId}`);
        }
        break;
    }
  };

  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};

// ===== 리소스 정리 =====
const stopObserving = () => {
  cleanupListener?.();
  cleanupListener = null;
  showExternalMask.value = false;
  activeRequestCount.value = 0;
  isLoading.value = false;
};

// ===== iframe 이벤트 핸들러 =====
const onIframeLoad = () => {
  devLog('✅ iframe 로드 완료');
  isLoading.value = false;
  stopObserving();
  cleanupListener = setupPostMessageListener();
  // JSP에서 자동으로 마스크 감시 시작하므로 별도 요청 불필요
};

const onIframeError = () => {
  console.error('iframe 로딩 실패');
  isLoading.value = false;
};

// ===== iframe 내부에 탭 변경 요청 (postMessage) =====
const sendTabChangeToIframe = tabIndex => {
  console.log('[AsisRenderer] 📤 sendTabChangeToIframe 호출:', { tabIndex, hasIframeRef: !!iframeRef.value, hasContentWindow: !!iframeRef.value?.contentWindow });
  try {
    if (iframeRef.value?.contentWindow) {
      iframeRef.value.contentWindow.postMessage(
        {
          type: 'CLICK_TAB_MENU',
          data: {
            tabIndex,
            pgmId: props.pgmId,
          },
        },
        '*',
      );
      console.log('[AsisRenderer] ✅ postMessage 전송 완료:', { tabIndex, pgmId: props.pgmId });
      return true;
    } else {
      console.log('[AsisRenderer] ⚠️ iframeRef 또는 contentWindow가 없음');
    }
  } catch (error) {
    console.log('[AsisRenderer] ❌ postMessage 전송 실패:', error.message);
  }
  return false;
};

// ===== 탭 변경 이벤트 리스너 =====
const setupTabChangedListener = () => {
  const handleTabChanged = event => {
    const { tabId } = event.detail;
    console.log('[AsisRenderer] 🔄 탭 변경 이벤트 수신:', { tabId });

    // 단순하게: 탭 클릭하면 바로 iframe에 전달
    updateTabIndex(tabId);
    sendTabChangeToIframe(tabId);
  };

  window.addEventListener('tabChanged', handleTabChanged);
  return () => window.removeEventListener('tabChanged', handleTabChanged);
};

// ===== 라이프사이클 =====
onMounted(async () => {
  // ★ 마운트 시 탭 인덱스 완전 초기화 (새 페이지는 항상 첫 번째 탭)
  moduleState.currentTabIndex = 0;
  moduleState.previousPgmId = props.pgmId;
  currentTabIndex.value = 0;  // ← 이것도 초기화!

  console.log('[AsisRenderer] 🟢 마운트됨:', {
    componentId,
    pgmId: props.pgmId,
    tabIndex: currentTabIndex.value
  });

  tabChangedListener = setupTabChangedListener();
  cleanupListener = setupPostMessageListener();
  localeChangedListener = handleAppLocaleChanged;
  window.addEventListener('appLocaleChanged', localeChangedListener);

  await nextTick();
  isMounted.value = true;

  // pendingTabChange 확인 (헤더 탭 클릭 후 이동한 경우)
  if (pendingTabChange.value?.pgmId === props.pgmId) {
    const pendingIdx = pendingTabChange.value.tabIndex;
    clearPendingTabChange();
    console.log('[AsisRenderer] ✅ pending 탭 사용:', pendingIdx);
    updateTabIndex(pendingIdx);
  }

  if (props.pgmId && !iframeSrc.value) {
    refreshIframe(currentTabIndex.value);
  }
});

onBeforeUnmount(() => {
  devLog(`🔴 AsisRenderer 언마운트됨 [${componentId}]`);
  stopObserving();
  tabChangedListener?.();
  tabChangedListener = null;
  if (localeChangedListener) {
    window.removeEventListener('appLocaleChanged', localeChangedListener);
    localeChangedListener = null;
  }
});

// ===== Watchers =====
watch(
  () => props.pgmId,
  newPgmId => {
    console.log('[AsisRenderer] 📌 pgmId watcher 실행:', {
      newPgmId,
      previousPgmId: moduleState.previousPgmId,
      isMounted: isMounted.value,
      willSkip: !isMounted.value || newPgmId === moduleState.previousPgmId
    });

    if (!isMounted.value || newPgmId === moduleState.previousPgmId) return;

    console.log('[AsisRenderer] 🔄 pgmId 변경됨 → refreshIframe 호출 예정:', newPgmId);
    moduleState.previousPgmId = newPgmId;

    // pendingTabChange 확인
    let tabIndex = 0;
    if (pendingTabChange.value?.pgmId === newPgmId) {
      tabIndex = pendingTabChange.value.tabIndex;
      clearPendingTabChange();
      console.log('[AsisRenderer] ✅ pending 탭 인덱스 사용:', tabIndex);
    }

    updateTabIndex(tabIndex);
    refreshIframe(tabIndex);
  },
  { immediate: true },
);

watch(
  () => props.meta,
  newMeta => {
    if (newMeta) {
      devLog('🔄 meta 변경 감지:', newMeta);
      refreshIframe();
    }
  },
);
</script>

<style scoped>
.iframe-renderer {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.iframe-content {
  width: 100%;
  height: 100% !important;
  min-height: 600px;
  border: 0;
  overflow: auto;
  position: relative;
}

.iframe-with-mask {
  z-index: 1001 !important;
}

.maskInner {
  position: fixed;
  top: 0;
  left: -100px;
  width: 150%;
  height: 150%;
  /* Vuetify v-overlay 색상과 동일하게 (rgb(33,33,33) + opacity 0.46) */
  background: rgba(33, 33, 33, 0.46);
  z-index: 1000;
  pointer-events: none;
}
</style>
